import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Filter, MapPin, Plus, RefreshCw, Search, X } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { useNotification } from '@/hooks/useNotification';
import { consoleLocationService } from '@/services/consoleLocationService';
import type { LocationSearchParams } from '@/types/consoleLocation';
import { isCustomLocationId, parseGeoCode } from '@/types/consoleLocation';
import { LocationDetailModal } from './components/LocationDetailModal';
import { CreateCustomLocationModal } from './components/CreateCustomLocationModal';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const formatCoord = (value: number | string | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return null;
  return n.toFixed(6);
};

// `geo_code` ("lat;lng") is the canonical carrier; latitude/longitude are forward-compat.
const coordsFromRow = (row: {
  geo_code?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
}): { lat: string | null; lng: string | null } => {
  const parsed = parseGeoCode(row.geo_code);
  if (parsed.latitude !== null && parsed.longitude !== null) {
    return { lat: formatCoord(parsed.latitude), lng: formatCoord(parsed.longitude) };
  }
  return { lat: formatCoord(row.latitude), lng: formatCoord(row.longitude) };
};

const useDebounced = <T,>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
};

export const LocationManagement = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const isAdmin = Array.isArray(user?.roles) && user!.roles.includes('ADMIN');

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounced(searchInput.trim(), SEARCH_DEBOUNCE_MS);
  const [district, setDistrict] = useState('');
  const [subdistrict, setSubdistrict] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, district, subdistrict]);

  const searchParams: LocationSearchParams = useMemo(
    () => ({
      q: debouncedSearch || undefined,
      district: district || undefined,
      subdistrict: subdistrict || undefined,
      page: currentPage,
      page_size: PAGE_SIZE,
    }),
    [debouncedSearch, district, subdistrict, currentPage],
  );

  const districtsQuery = useQuery({
    queryKey: ['console-location', 'districts'],
    queryFn: () => consoleLocationService.getDistricts(),
    staleTime: 60 * 60 * 1000,
  });

  const subdistrictsQuery = useQuery({
    queryKey: ['console-location', 'subdistricts', district],
    queryFn: () => consoleLocationService.getSubdistricts(district),
    enabled: district.length > 0,
    staleTime: 60 * 60 * 1000,
  });

  const searchQuery = useQuery({
    queryKey: ['console-location', 'search', searchParams],
    queryFn: () => consoleLocationService.search(searchParams),
    placeholderData: (prev) => prev,
    staleTime: 30 * 1000,
  });

  const items = searchQuery.data?.list ?? [];
  const total = searchQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const refreshCacheMutation = useMutation({
    mutationFn: () => consoleLocationService.refreshCache(),
    onSuccess: (response) => {
      showSuccess('Cache refresh queued', response.message || 'Data will be cached.');
    },
    onError: (err: any) => {
      showError('Cache refresh failed', err?.message || 'Could not refresh location cache.');
    },
  });

  const clearFilters = () => {
    setSearchInput('');
    setDistrict('');
    setSubdistrict('');
    setCurrentPage(1);
  };

  const filtersActive =
    Boolean(searchInput) || Boolean(district) || Boolean(subdistrict);

  const rangeStart = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, total);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-rgf-gray-900 sm:text-3xl">
            Location Management
          </h1>
          <p className="mt-1 text-sm text-rgf-gray-500">
            Browse Punjab master locations, search by name, and register custom locations
            under existing subdistricts.
          </p>
        </div>
        <div className="mt-4 flex gap-2 md:mt-0 md:ml-4">
          {isAdmin && (
            <>
              <Button
                variant="secondary"
                size="sm"
                icon={<RefreshCw className={`h-4 w-4 ${refreshCacheMutation.isPending ? 'animate-spin' : ''}`} />}
                onClick={() => refreshCacheMutation.mutate()}
                disabled={refreshCacheMutation.isPending}
              >
                {refreshCacheMutation.isPending ? 'Refreshing…' : 'Refresh Cache'}
              </Button>
              <Button
                size="sm"
                icon={<Plus className="h-4 w-4" />}
                onClick={() => setShowCreateModal(true)}
              >
                Add Custom Location
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters + search */}
      <div className="card">
        <div className="card-body">
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search by location name (English)…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startIcon={<Search className="h-4 w-4" />}
                />
              </div>
              <Button
                variant="secondary"
                icon={<Filter className="h-4 w-4" />}
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
              {filtersActive && (
                <Button
                  variant="ghost"
                  icon={<X className="h-4 w-4" />}
                  onClick={clearFilters}
                >
                  Clear
                </Button>
              )}
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-rgf-gray-200">
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    District
                  </label>
                  <select
                    value={district}
                    onChange={(e) => {
                      setDistrict(e.target.value);
                      setSubdistrict('');
                    }}
                    disabled={districtsQuery.isLoading}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    <option value="">
                      {districtsQuery.isLoading ? 'Loading…' : 'All districts'}
                    </option>
                    {(districtsQuery.data ?? []).map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Subdistrict
                  </label>
                  <select
                    value={subdistrict}
                    onChange={(e) => setSubdistrict(e.target.value)}
                    disabled={!district || subdistrictsQuery.isLoading}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500 disabled:bg-rgf-gray-50 disabled:text-rgf-gray-500"
                  >
                    <option value="">
                      {!district
                        ? 'Pick a district first'
                        : subdistrictsQuery.isLoading
                          ? 'Loading…'
                          : 'All subdistricts'}
                    </option>
                    {(subdistrictsQuery.data ?? []).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <Button
                    variant="secondary"
                    onClick={clearFilters}
                    disabled={!filtersActive}
                    className="w-full"
                  >
                    Clear All Filters
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results table */}
      <div className="card">
        <div className="card-body p-0">
          {searchQuery.isLoading && !searchQuery.data ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-rgf-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-rgf-gray-600">Loading locations…</p>
            </div>
          ) : searchQuery.error ? (
            <div className="p-8 text-center space-y-3">
              <p className="text-rgf-gray-700">
                {(searchQuery.error as Error)?.message || 'Failed to load locations.'}
              </p>
              <Button onClick={() => searchQuery.refetch()}>Try Again</Button>
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-rgf-gray-600">
              <MapPin className="h-10 w-10 mx-auto mb-3 text-rgf-gray-400" />
              <p>No locations found.</p>
              {filtersActive && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="mt-2 text-sm font-medium text-rgf-primary-600 hover:text-rgf-primary-500"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-rgf-gray-200">
                <thead className="bg-rgf-gray-50">
                  <tr>
                    <th className="table-header">Location</th>
                    <th className="table-header">District</th>
                    <th className="table-header">Subdistrict</th>
                    <th className="table-header">Pincode</th>
                    <th className="table-header">Latitude</th>
                    <th className="table-header">Longitude</th>
                    <th className="table-header">Type</th>
                    <th className="table-header">ID</th>
                    <th className="table-header text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-rgf-gray-200">
                  {items.map((row) => {
                    const { lat: latValue, lng: lngValue } = coordsFromRow(row);
                    const isCustom = isCustomLocationId(row.id) || row.location_code === 0;

                    return (
                      <tr key={row.id} className="hover:bg-rgf-gray-50">
                        <td className="table-cell">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-rgf-gray-900">
                              {row.location_name_in_english}
                            </span>
                            {row.location_alias_name && (
                              <span className="text-xs text-rgf-gray-500">
                                Alias: {row.location_alias_name}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="table-cell text-sm text-rgf-gray-700">
                          {row.district_name_in_english}
                        </td>
                        <td className="table-cell text-sm text-rgf-gray-700">
                          {row.subdistrict_name_in_english}
                        </td>
                        <td className="table-cell text-sm font-mono text-rgf-gray-700">
                          {row.pincode ?? '—'}
                        </td>
                        <td className="table-cell text-xs font-mono">
                          <CoordCell value={latValue} />
                        </td>
                        <td className="table-cell text-xs font-mono">
                          <CoordCell value={lngValue} />
                        </td>
                        <td className="table-cell">
                          {isCustom ? (
                            <span className="badge badge-warning">Custom</span>
                          ) : (
                            <span className="badge badge-success">Master</span>
                          )}
                        </td>
                        <td className="table-cell text-xs font-mono text-rgf-gray-500">
                          {row.id}
                        </td>
                        <td className="table-cell text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDetailId(row.id)}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {total > 0 && (
          <div className="px-6 py-3 bg-rgf-gray-50 border-t border-rgf-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-rgf-gray-700">
                Showing {rangeStart.toLocaleString()} to {rangeEnd.toLocaleString()} of{' '}
                {total.toLocaleString()} results
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={currentPage <= 1 || searchQuery.isFetching}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-sm text-rgf-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={currentPage >= totalPages || searchQuery.isFetching}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <LocationDetailModal
        isOpen={detailId !== null}
        lgdCode={detailId}
        onClose={() => setDetailId(null)}
      />

      {isAdmin && (
        <CreateCustomLocationModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={(id) => setDetailId(id)}
          initialDistrict={district}
          initialSubdistrict={subdistrict}
        />
      )}
    </div>
  );
};

const CoordCell = ({ value }: { value: string | null }) => {
  if (value === null) return <span className="text-rgf-gray-400">—</span>;
  return <span className="text-rgf-gray-700">{value}</span>;
};
